import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Loader2, ShieldAlert, Clock,
  AlertTriangle, XCircle, RefreshCw, Eye
} from 'lucide-react';

export default function SLARules() {
  const [rules, setRules] = useState([]);
  const [violations, setViolations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', entity_type: 'lead',
    min_score: 70, max_idle_hours: 2, escalation_hours: 4,
    auto_reassign: false, notify_manager: true, is_active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/api/sla-rules/');
      setRules(res.data.data || []);
    } catch (e) {
      toast.error('Failed to load SLA rules');
    } finally {
      setLoading(false);
    }
  };

  const checkViolations = async () => {
    setChecking(true);
    try {
      const res = await api.get('/api/sla-rules/violations');
      setViolations(res.data);
      if (res.data.total === 0) toast.success('No SLA violations detected');
      else toast.warning(`${res.data.total} SLA violation(s) found`);
    } catch (e) {
      toast.error('Failed to check violations');
    } finally {
      setChecking(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', entity_type: 'lead', min_score: 70, max_idle_hours: 2, escalation_hours: 4, auto_reassign: false, notify_manager: true, is_active: true });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (rule) => {
    setForm({
      name: rule.name, description: rule.description || '', entity_type: rule.entity_type,
      min_score: rule.min_score, max_idle_hours: rule.max_idle_hours,
      escalation_hours: rule.escalation_hours, auto_reassign: rule.auto_reassign,
      notify_manager: rule.notify_manager, is_active: rule.is_active
    });
    setEditId(rule.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/api/sla-rules/${editId}`, form);
        toast.success('SLA rule updated');
      } else {
        await api.post('/api/sla-rules/', form);
        toast.success('SLA rule created');
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SLA rule?')) return;
    try {
      await api.delete(`/api/sla-rules/${id}`);
      toast.success('SLA rule deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="p-6 space-y-4" data-testid="sla-loading"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="sla-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">SLA Rules</h1>
          <p className="text-sm text-muted-foreground mt-1">Define response time SLAs for hot leads and deals with auto-escalation.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={checkViolations} disabled={checking} data-testid="check-violations-btn">
            {checking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Check Violations
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-sla-btn">
            <Plus className="h-4 w-4 mr-2" /> New SLA Rule
          </Button>
        </div>
      </div>

      {/* Violations Panel */}
      {violations && violations.total > 0 && (
        <Card className="border-l-4 border-l-red-500" data-testid="violations-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-red-600 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" /> {violations.total} SLA Violation(s)
              <Badge variant="destructive">{violations.critical_count} critical</Badge>
              <Badge variant="secondary">{violations.warning_count} warning</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-auto">
              {violations.data.map((v, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    {v.severity === 'critical' ? <XCircle className="h-4 w-4 text-red-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                    <span className="font-medium">{v.entity_name}</span>
                    <Badge variant="outline" className="text-xs capitalize">{v.entity_type}</Badge>
                    {v.ai_score && <Badge variant="secondary" className="text-xs">Score: {v.ai_score}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> Idle {v.hours_idle}h (limit: {v.max_idle_hours}h)
                    <Badge variant={v.severity === 'critical' ? 'destructive' : 'secondary'} className="text-xs">{v.severity}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      {showForm && (
        <Card data-testid="sla-rule-form">
          <CardHeader><CardTitle className="text-base">{editId ? 'Edit' : 'Create'} SLA Rule</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input data-testid="sla-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Hot Lead Response SLA" /></div>
              <div><Label>Entity Type</Label>
                <Select value={form.entity_type} onValueChange={v => setForm(f => ({ ...f, entity_type: v }))}>
                  <SelectTrigger data-testid="sla-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="deal">Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Min AI Score</Label><Input data-testid="sla-min-score" type="number" min={0} max={100} value={form.min_score} onChange={e => setForm(f => ({ ...f, min_score: parseInt(e.target.value) || 0 }))} /></div>
              <div><Label>Warning After (hours)</Label><Input data-testid="sla-idle-hours" type="number" min={0.5} step={0.5} value={form.max_idle_hours} onChange={e => setForm(f => ({ ...f, max_idle_hours: parseFloat(e.target.value) || 2 }))} /></div>
              <div><Label>Escalate After (hours)</Label><Input data-testid="sla-escalation-hours" type="number" min={1} step={0.5} value={form.escalation_hours} onChange={e => setForm(f => ({ ...f, escalation_hours: parseFloat(e.target.value) || 4 }))} /></div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch data-testid="sla-notify" checked={form.notify_manager} onCheckedChange={v => setForm(f => ({ ...f, notify_manager: v }))} /><Label>Notify Manager</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="sla-reassign" checked={form.auto_reassign} onCheckedChange={v => setForm(f => ({ ...f, auto_reassign: v }))} /><Label>Auto-Reassign</Label></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="sla-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editId ? 'Update' : 'Create'} Rule</Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rule List */}
      {rules.length > 0 ? (
        <div className="space-y-3" data-testid="sla-rules-list">
          {rules.map(rule => (
            <Card key={rule.id} data-testid={`sla-rule-${rule.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-amber-500" />
                      <span className="font-medium">{rule.name}</span>
                      <Badge variant={rule.is_active ? 'default' : 'secondary'}>{rule.is_active ? 'Active' : 'Inactive'}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{rule.entity_type}</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>Min Score: {rule.min_score}</span>
                      <span>Warning: {rule.max_idle_hours}h</span>
                      <span>Escalation: {rule.escalation_hours}h</span>
                      {rule.auto_reassign && <Badge variant="outline" className="text-xs">Auto-Reassign</Badge>}
                      {rule.notify_manager && <Badge variant="outline" className="text-xs">Notify Manager</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(rule)}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(rule.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !showForm && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No SLA rules yet. Create one to monitor response times for hot leads.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
